import { useEffect, useState } from "react";
import ModuleContainer from "./ModuleContainer";
import { supabase } from "../../lib/supabase";
import { Bubble } from "react-chartjs-2";
import React from "react";
import { colors } from "../../lib/designTokens";

import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend, ChartDataLabels);

type MarketRow = {
  brand: string;
  visibility: number;
  performance: number;
};

type EnrichedRow = MarketRow & {
  isCustomer: boolean;
};

type ModuleStatus = "loading" | "ready" | "empty" | "error" | "unavailable";

export default function MarketMap({
  account,
  demoMode = false,
  customerBrand,
  runId,
}: {
  account?: string;
  demoMode?: boolean;
  customerBrand?: string;
  runId?: string;
}) {
const [data, setData] = useState<MarketRow[]>([]);
const [status, setStatus] = useState<ModuleStatus>("loading");
const [statusMessage, setStatusMessage] = useState("");
  // ✅ Strong normalization
const formatBrandName = (brand?: string) => {
  if (!brand) return "";

  const knownBrands: Record<string, string> = {
    forney: "Forney",
    "miller electric": "Miller Electric",
    "lincoln electric": "Lincoln Electric",
    esab: "ESAB",
    hobart: "Hobart",
  };

  const normalized = brand.toLowerCase().trim();

  if (knownBrands[normalized]) return knownBrands[normalized];

  return brand
    .split(" ")
    .map((word) =>
      word.length ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(" ");
};

  const normalize = (str?: string) =>
  str
    ?.toLowerCase()
    .replace(/inc\.?|corp\.?|ltd\.?|llc/g, "") // remove suffixes properly
    .replace(/[^a-z0-9]/g, "") // remove ALL punctuation
    .trim();
  const isCustomerBrand = (brand?: string) => {
    if (!brand || !customerBrand) return false;
    return normalize(brand) === normalize(customerBrand);
  };

  const displayName = (brand: string, index: number, isCustomer: boolean) => {
    if (!demoMode) return brand;
    if (isCustomer) return brand;
    return `Competitor ${String.fromCharCode(65 + index)}`;
  };

  useEffect(() => {
  if (!account) {
    setData([]);
    setStatus("unavailable");
    setStatusMessage("This module is not available for this account yet.");
    return;
  }

  const fetchData = async () => {
  setData([]);
  setStatus("loading");
  setStatusMessage("");

  let query = supabase
    .from("toren_competitive_perception_role_v1")
    .select("brand, visibility, performance")
    .eq("account_id", account);

  if (runId) {
    query = query.eq("run_id", runId);
  }

  const { data: marketData, error } = await query;

  if (error) {
    console.error("[AIMarketMap] Query error", error);
    setData([]);
    setStatus("error");
    setStatusMessage("This module could not load right now.");
    return;
  }

  if (!marketData?.length) {
    setData([]);
    setStatus("empty");
    setStatusMessage("Not enough data available yet.");
    return;
  }

  const rows: MarketRow[] = marketData
    .map((row: any) => ({
      brand: row.brand || "Unknown",
      visibility: Number(row.visibility) || 0,
      performance: Number(row.performance) || 0,
    }))
    .filter(
      (row) =>
        row.brand &&
        row.brand !== "Unknown" &&
        (row.visibility > 0.5 || isCustomerBrand(row.brand))
    );

  const customerRow = rows.find((row) => isCustomerBrand(row.brand));

  const topRows = rows
    .filter((row) => !isCustomerBrand(row.brand))
    .sort((a, b) => b.visibility - a.visibility)
    .slice(0, customerRow ? 11 : 12);

  const formatted: MarketRow[] = customerRow
    ? [...topRows, customerRow]
    : topRows;

  if (!formatted.length) {
    setData([]);
    setStatus("empty");
    setStatusMessage("Not enough data available yet.");
    return;
  }

  setData(formatted);
  setStatus("ready");
};

        fetchData();
  }, [account, customerBrand, runId]);

  if (status !== "ready" || !data.length) {
  return (
    <ModuleContainer
      title="AI Perception"
      state={status}
      message={statusMessage}
    />
  );
}

  // ✅ Enrich ONCE
  const enrichedData: EnrichedRow[] = data.map((row) => ({
    ...row,
    isCustomer: isCustomerBrand(row.brand),
  }));

  // ✅ Label only the most useful competitor points
const competitorRows = enrichedData.filter((d) => !d.isCustomer);

const labelBrands = new Set<string>();

const highestPerformance = [...competitorRows].sort(
  (a, b) => b.performance - a.performance
)[0];

const highestVisibility = [...competitorRows].sort(
  (a, b) => b.visibility - a.visibility
)[0];

const balancedLeader = [...competitorRows].sort(
  (a, b) =>
    b.visibility + b.performance - (a.visibility + a.performance)
)[0];

[highestPerformance, highestVisibility, balancedLeader]
  .filter(Boolean)
  .forEach((row) => labelBrands.add(row.brand));

const maxVisibility =
  enrichedData.length > 0
    ? Math.max(...enrichedData.map((d) => d.visibility))
    : 100;

const xAxisMax =
  maxVisibility <= 25 ? 50 :
  maxVisibility <= 50 ? 75 :
  100;

  const chartData = {
    datasets: [
      {
        data: enrichedData.map((row) => ({
          x: row.visibility,
          y: row.performance,
          r: row.isCustomer
              ? Math.min(26, row.visibility * 0.35 + 10)
              : Math.min(20, row.visibility * 0.28 + 6),
        })),
        backgroundColor: enrichedData.map((row) =>
  row.isCustomer
    ? "rgba(59,130,246,0.95)" // primary blue (YOU)
    : "rgba(59,130,246,0.25)" // lighter blue (competitors)
),
borderColor: enrichedData.map((row) =>
  row.isCustomer ? colors.primary : "#ffffff"
),
        hoverBorderWidth: enrichedData.map((row) =>
  row.isCustomer ? 6 : 1
),
      },
    ],
  };

  const options: any = {
  responsive: true,
  maintainAspectRatio: false,

  layout: {
    padding: {
      top: 18,
      right: 36,
      bottom: 8,
      left: 8,
    },
  },

  interaction: {
    mode: "nearest",
    intersect: true,
  },

  scales: {
      x: {
        title: {
          display: true,
          text: "AI Presence (% of prompts)",
        },
        min: 0,
        max: xAxisMax,
        ticks: {
          callback: (value: any) => `${Number(value).toFixed(0)}`
        },
      },
      y: {
        title: {
          display: true,
          text: "AI Recommendation Strength (0–100)",
        },
        min: 0,
        max: 100,
      },
    },
    
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const row = enrichedData[context.dataIndex];
            if (!row) return "";

            return [
              displayName(row.brand, context.dataIndex, row.isCustomer),
              `Visibility: ${row.visibility.toFixed(1)}%`,
              `AI Performance: ${row.performance.toFixed(0)}`,
              "",
              row.performance > 80
                ? "🔥 Highly recommended by AI"
                : row.performance > 60
                ? "👍 Frequently recommended"
                : "⚠️ Low recommendation strength",
            ];
          },
        },
      },
      datalabels: {
        formatter: (_: any, context: any) => {
  const row = enrichedData[context.dataIndex];
  if (!row) return "";

  if (row.isCustomer) return "YOU";

  if (labelBrands.has(row.brand)) {
    const label = displayName(row.brand, context.dataIndex, row.isCustomer);
    return label.length > 12 ? label.slice(0, 10) + "…" : label;
  }

  return "";
},
         color: "#111",
  anchor: "end",
  align: "top",
  offset: 4,
  clip: false,
  clamp: true,
  font: {
    weight: "bold",
    size: 10,
        },
      },
    },
  };
function getMarketInsight() {
  if (!enrichedData.length) return "";

  const customer = enrichedData.find((d) => d.isCustomer);

  if (!customer) {
    const leader = [...enrichedData].sort((a, b) => b.performance - a.performance)[0];
    return `${leader.brand} shows the strongest AI recommendation strength, indicating a leading perception advantage.`;
  }

const { visibility, performance } = customer;
const brand = formatBrandName(customer.brand);

  // 🎯 Quadrant logic

  // High visibility, low performance
  if (visibility > 10 && performance < 60) {
    return `${brand} appears frequently but is not strongly recommended, suggesting competitors are outperforming you in AI preference.`;
  }

  // Low visibility, high performance
  if (visibility < 5 && performance > 70) {
    return `${brand} is highly recommended when mentioned, but appears infrequently, indicating an opportunity to increase visibility.`;
  }

  // Strong both
  if (visibility > 10 && performance > 75) {
    return `${brand} is both highly visible and strongly recommended, indicating a leading position in AI-driven results.`;
  }

  // Weak both
  if (visibility < 5 && performance < 60) {
    return `${brand} has low visibility and weak recommendation strength, indicating limited presence and competitiveness in AI responses.`;
  }

  // Default
  return `${brand} shows moderate visibility and recommendation strength, with opportunities to improve both presence and AI preference.`;
}
  return (
    <ModuleContainer title="AI Perception">
  <div style={{ marginBottom: 12 }}>
    <p style={{ margin: 0, color: colors.textSecondary, fontSize: 14 }}>
      How often each brand appears versus how strongly AI recommends it across competitive buyer-journey prompts.
    </p>

    <div
  style={{
    marginTop: 10, 
    marginBottom: 6, 
    fontSize: 13,
    color: colors.textSecondary,
    display: "flex",
    flexDirection: "column",
    gap: 4, 
  }}
>
  <span>⬆️ More positive AI recommendations</span>
  <span>➡️ More frequent presence</span>
</div>
  </div>

  <div style={{ height: 460, width: "100%" }}>
    <Bubble data={chartData} options={options} />
  </div>

  <div style={{ marginTop: 10, fontSize: 13 }}>
    <span style={{ color: colors.primary, fontWeight: "bold" }}>● You</span>
    <span style={{ marginLeft: 12, color: "#3b82f6" }}>
      ● Competitors
    </span>
  </div>
 <div
  style={{
    marginTop: 10,
    padding: "12px 14px",
    background: colors.background,
border: `1px solid ${colors.border}`,
    borderRadius: 10
  }}
>
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: colors.textSecondary,
      marginBottom: 4,
      letterSpacing: "0.04em"
    }}
  >
   🧠 PERCEPTION INSIGHT
  </div>
  <div
    style={{
      fontSize: 13,
      color: colors.textPrimary,
      lineHeight: 1.4
    }}
  >
{getMarketInsight()}  
</div>
</div>
</ModuleContainer>
  );
}